import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { api, getToken, setToken } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [localeLocked, setLocaleLocked] = useState(false);
  const [lockedLocale, setLockedLocale] = useState(null);
  const { i18n } = useTranslation();

  const applyLocale = useCallback(
    (_locale) => {
      // Locale is hardcoded to en-US in this build — ignore any server-sent value.
    },
    []
  );

  const refresh = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setUser(null);
      setOrganization(null);
      setLocaleLocked(false);
      setLockedLocale(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setOrganization(data.organization || null);
      setLocaleLocked(!!data.locale_locked);
      setLockedLocale(data.locked_locale || null);
      // Server-locked locale wins; else honor user preference.
      applyLocale(data.locked_locale || data.locale || data.user?.locale);
    } catch (_) {
      setToken(null);
      setUser(null);
      setOrganization(null);
      setLocaleLocked(false);
      setLockedLocale(null);
    } finally {
      setLoading(false);
    }
  }, [applyLocale]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const persistAuthResult = useCallback(
    (data) => {
      setToken(data.access_token);
      setUser(data.user);
      setOrganization(data.organization || null);
      applyLocale(data.user?.locale);
    },
    [applyLocale]
  );

  const login = useCallback(
    async (email, password) => {
      const { data } = await api.post("/auth/login", { email, password });
      persistAuthResult(data);
      return data;
    },
    [persistAuthResult]
  );

  const signupEmployer = useCallback(
    async (payload) => {
      const { data } = await api.post("/auth/signup/employer", payload);
      persistAuthResult(data);
      return data;
    },
    [persistAuthResult]
  );

  const signupAgency = useCallback(
    async (payload) => {
      const { data } = await api.post("/auth/signup/agency", payload);
      persistAuthResult(data);
      return data;
    },
    [persistAuthResult]
  );

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setOrganization(null);
  }, []);

  const updateLocale = useCallback(
    async (_locale) => {
      // No-op — locale is hardcoded to en-US in this build.
    },
    []
  );

  const setOrganizationDirect = useCallback((org) => {
    setOrganization(org);
  }, []);

  const value = {
    user,
    organization,
    loading,
    localeLocked,
    lockedLocale,
    login,
    logout,
    signupEmployer,
    signupAgency,
    refresh,
    updateLocale,
    setOrganization: setOrganizationDirect,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
